import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Header, Pagination, PaginationProps } from 'semantic-ui-react';
import LoaderComponent from 'components/LoaderComponent';
import styled from 'styled-components';
import { numberFormatter } from 'utils/fomattingUtils';
import axios from 'services/http';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import {
  DayStatisticResponse,
  PdfResponse,
  ProcedureGroupStatistic,
  TariffDto,
  TariffStatistic
} from 'ts-types/api.types';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import { RouteComponentProps } from 'react-router';
import { CancelTokenSource } from 'axios';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';

const StyledTable = styled(VirtualizedTable)`

  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__row:last-child {
    font-weight: 700;
    background-color: rgba(0, 0, 0, .2);

    &:hover {
      background-color: rgba(0, 0, 0, .2) !important;
    }
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
    text-align: right;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const MainContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledFormContainer = styled(FormContainer)`
  height: 100%;
`;

const BillsContainer = styled.div`
  height: 90%;
  min-height: 200px;
`;

const StyledFormContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
  height: 100%;
  align-items: center;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }
`;

const StyledCell = styled.div`
  text-align: right;
`;

const StyledTotal = styled(Header)`
  text-align: right;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  statisticsData: DayStatisticResponse,
  statisticsLoading: boolean,
  tariffs: Array<TariffDto>,
  activePage?: any,
  handlePaginationChange: (e: any, data: PaginationProps) => void,
  setErrorMessage: (errorMessage?: string) => void
}

interface State {
  proceduresProtocolPdfData?: PdfResponse,
  proceduresProtocolPdfModalOpen: boolean,
  cancelTokenSource: CancelTokenSource
}

class DayStatisticTableSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      proceduresProtocolPdfModalOpen: false,
      cancelTokenSource: cancelTokenSource
    };
  }

  tariffStatisticsRowGetter = (bills: Array<ProcedureGroupStatistic>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  amountCellRenderer = ({cellData}: any) => {
    return <StyledCell>{numberFormatter.format(cellData)}</StyledCell>;
  };

  tariffStatisticsRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  render(): React.ReactNode {
    return (
        <React.Fragment>
          {this.renderTariffData()}
        </React.Fragment>
    )
  };

  fillTariffStatsData = (trfStats: TariffStatistic, tariffStatsData: Array<ProcedureGroupStatistic>) => {
    tariffStatsData.push({name: "Total Unknown group", amount: trfStats.variousTotal})
    tariffStatsData.push({name: "Total add. labor", amount: trfStats.additionalLaborTotal})
    tariffStatsData.push({name: "Total add. material", amount: trfStats.additionalMaterialTotal})
    tariffStatsData.push({name: "Total Other", amount: trfStats.otherTotal})
    tariffStatsData.push({name: "Total Medicine", amount: trfStats.medicineTotal})
    tariffStatsData.push({name: "Total Material", amount: trfStats.materialTotal})
    tariffStatsData.push({name: "Total", amount: trfStats.tariffTotal})
  }

  renderTariffData = (): React.ReactNode => {
    const {statisticsData, tariffs, statisticsLoading, handlePaginationChange, activePage, getMessage} = this.props;

    let tariffStatistics: Array<any> = [];

    if (statisticsData.tariffStatistics && statisticsData.tariffStatistics.length > 0) {

      tariffStatistics = statisticsData.tariffStatistics.map(trfStats => {
        const tariffId = trfStats.tariffId;
        const tariff = tariffs.find(trf => trf.id === tariffId);
        const tariffName = tariff ? tariff.description : "";

        let tariffStatsData = Object.values(trfStats.procedureGroupStatistics);
        this.fillTariffStatsData(trfStats, tariffStatsData);

        return this.renderTariffStatisticsTable(tariffName, tariffStatsData);
      })
    }

    return (
        <MainContainer>
          <StyledFormContainer>
            <FormContent>
              <StyledFormContent>
                <StyledContainer style={{width: "100%"}}>
                  {statisticsLoading &&
                  <LoaderComponent
                      message={`${getMessage("loading")} ${getMessage("dayStatistic.tariffStats").toLowerCase()}`}
                  />
                  }
                  {activePage && !statisticsLoading &&
                  <React.Fragment>
                    <BillsContainer>
                      {tariffStatistics[activePage - 1]}
                    </BillsContainer>
                    <StyledTotal as="h3">
                      Total: {numberFormatter.format(statisticsData.total)}
                    </StyledTotal>
                  </React.Fragment>
                  }
                </StyledContainer>
                {activePage &&
                <Pagination
                    activePage={activePage}
                    boundaryRange={1}
                    onPageChange={handlePaginationChange}
                    size='small'
                    siblingRange={1}
                    totalPages={tariffStatistics.length}
                    ellipsisItem={undefined}
                    firstItem={undefined}
                    lastItem={undefined}
                    prevItem={undefined}
                    nextItem={undefined}
                />
                }
              </StyledFormContent>
            </FormContent>
          </StyledFormContainer>
        </MainContainer>
    );
  };

  renderTariffStatisticsTable = (tariffName: string, tariffStatistics: Array<ProcedureGroupStatistic>): React.ReactNode => {

    const {getMessage} = this.props;

    const rowCount = tariffStatistics.length;

    return (
        <StyledTable
            rowGetter={this.tariffStatisticsRowGetter(tariffStatistics)}
            rowRenderer={this.tariffStatisticsRowRenderer}
            rowCount={tariffStatistics.length > 0 ? rowCount : 0}
            columns={[
              {
                width: 500,
                label: tariffName,
                dataKey: "name"
              },
              {
                width: 2000,
                label: getMessage("amount"),
                dataKey: "amount",
                cellRenderer: this.amountCellRenderer
              }
            ]}
        />
    )
  };

}

export default withAuthContext(withRouterWorkaround(withMessages(DayStatisticTableSegment)));
